import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Phone, Calendar, Clock, Stethoscope, Building2, Tag } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { getBookingById, cancelBooking } from '../lib/api';

const STATUS_STYLES = {
  pending:   'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  pending:   'Awaiting Clinic Confirmation',
  confirmed: 'Confirmed',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const BookingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    getBookingById(id)
      .then((res) => setBooking(res.data?.data || res.data))
      .catch((err) => {
        if (err?.response?.status === 401) {
          // Redirect to login, preserve intended destination
          navigate('/login');
        } else if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          toast.error('Failed to load booking details');
          setNotFound(true);
        }
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await cancelBooking(id);
      setBooking((prev) => ({ ...prev, status: 'cancelled' }));
      toast.success('Booking cancelled');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#1FA7A1] border-t-transparent" />
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !booking) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Booking not found</h1>
          <p className="text-gray-500 mb-6">This booking may have been removed or doesn't exist.</p>
          <Link to="/profile">
            <Button className="bg-[#1FA7A1] hover:bg-[#178a85]">Back to Profile</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const status = booking.status?.toLowerCase() || 'pending';
  const displayDate = booking.date || booking.appointmentDate || booking.preferredDate;
  const displayTime = booking.time || booking.appointmentTime || booking.preferredTime;
  const { clinic, treatment, doctor } = booking;
  const canCancel = status === 'pending' || status === 'confirmed';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Back link */}
        <Link
          to="/profile"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#1FA7A1] mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Profile
        </Link>

        {/* Heading + Status Badge */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Booking Details</h1>
          <span
            className={`px-3 py-1.5 rounded-full text-sm font-semibold ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'}`}
            data-testid="booking-status-badge"
          >
            {STATUS_LABELS[status] || status}
          </span>
        </div>

        <div className="space-y-4">
          {/* Clinic Card */}
          {clinic && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5" data-testid="clinic-card">
              <div className="flex items-start gap-4">
                {clinic.imageUrl ? (
                  <img
                    src={clinic.imageUrl}
                    alt={clinic.name}
                    className="w-[60px] h-[60px] rounded-full object-cover flex-shrink-0"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-[60px] h-[60px] rounded-full bg-[#1FA7A1]/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-7 w-7 text-[#1FA7A1]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-base">{clinic.name}</p>
                  {clinic.address && (
                    <p className="text-sm text-gray-500 flex items-start gap-1 mt-1">
                      <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5 text-gray-400" />
                      {clinic.address}
                    </p>
                  )}
                  {clinic.phone && (
                    <a
                      href={`tel:${clinic.phone}`}
                      className="text-sm text-gray-500 flex items-center gap-1 mt-1 hover:text-[#1FA7A1]"
                    >
                      <Phone className="h-4 w-4 text-gray-400" />
                      {clinic.phone}
                    </a>
                  )}
                  {(clinic.slug || clinic.id) && (
                    <Link
                      to={`/clinic/${clinic.slug || clinic.id}`}
                      className="text-sm text-[#1FA7A1] font-medium hover:underline mt-2 inline-block"
                    >
                      View Clinic →
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Booking Info Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-3" data-testid="booking-info-card">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Booking Info</p>
            {displayDate && (
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <Calendar className="h-4 w-4 text-[#1FA7A1] flex-shrink-0" />
                {new Date(displayDate).toLocaleDateString('en-US', {
                  weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
                })}
              </div>
            )}
            {displayTime && (
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <Clock className="h-4 w-4 text-[#1FA7A1] flex-shrink-0" />
                {displayTime}
              </div>
            )}
            {treatment?.name && (
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <Tag className="h-4 w-4 text-[#1FA7A1] flex-shrink-0" />
                {treatment.name}
              </div>
            )}
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <Stethoscope className="h-4 w-4 text-[#1FA7A1] flex-shrink-0" />
              {doctor?.name ? (
                <span>Dr. {doctor.name}{doctor.specialty ? ` — ${doctor.specialty}` : ''}</span>
              ) : (
                <span className="text-gray-400">No doctor assigned</span>
              )}
            </div>
          </div>

          {/* Patient Notes */}
          {booking.notes && (
            <div className="bg-gray-50 rounded-2xl border border-gray-100 p-5" data-testid="patient-notes">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Your Notes</p>
              <p className="text-sm text-gray-700">{booking.notes}</p>
            </div>
          )}

          {/* Clinic Notes */}
          {booking.clinicNotes && (
            <div className="bg-teal-50 rounded-2xl border border-[#1FA7A1]/20 p-5" data-testid="clinic-notes">
              <p className="text-xs font-semibold text-[#1FA7A1] uppercase tracking-wide mb-2">Clinic Notes</p>
              <p className="text-sm text-gray-700">{booking.clinicNotes}</p>
            </div>
          )}

          {/* Clinic confirmed via email timestamp */}
          {booking.clinicConfirmedViaEmailAt && (
            <p className="text-xs text-gray-400 px-1" data-testid="clinic-email-confirmed-at">
              Clinic confirmed via email on {new Date(booking.clinicConfirmedViaEmailAt).toLocaleString()}
            </p>
          )}

          {/* Cancellation info — only shown when cancelled */}
          {status === 'cancelled' && (
            <div className="bg-red-50 rounded-2xl border border-red-100 p-5 space-y-2" data-testid="cancellation-info">
              <p className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-1">Cancellation Details</p>
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Reason: </span>
                {booking.cancellationReason || 'No reason provided'}
              </p>
              {booking.autoCancelledAt && (
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Auto-cancelled at: </span>
                  {new Date(booking.autoCancelledAt).toLocaleString()}
                </p>
              )}
            </div>
          )}

          {/* Price */}
          {booking.totalPrice > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 text-center" data-testid="booking-price">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total Price</p>
              <p className="text-3xl font-bold text-[#1FA7A1]">
                {booking.currency === 'USD' ? '$' : ''}
                {Number(booking.totalPrice).toLocaleString()} {booking.currency || 'USD'}
              </p>
            </div>
          )}

          {/* Cancel Button */}
          {canCancel && (
            <Button
              variant="outline"
              className="w-full border-red-200 text-red-500 hover:bg-red-50"
              onClick={handleCancel}
              disabled={cancelling}
              data-testid="cancel-booking-btn"
            >
              {cancelling ? 'Cancelling...' : 'Cancel Booking'}
            </Button>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BookingDetailPage;
